/**
 * ProposalPage — E18. Printable NECS FACILITY CARE PROPOSAL (admin only).
 * Print via browser → PDF; email via the Sales panel or button below.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch } from '../lib/apiFetch';
import { toast } from 'sonner';

const GOLD = '#C9A962';

export default function ProposalPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    apiFetch(`/api/commercial-quote/proposals/${id}`).then(setP).catch((e) => setErr(e.detail || 'Not found'));
  }, [id]);

  if (err) return <div className="min-h-screen flex items-center justify-center bg-black text-zinc-400">{err}</div>;
  if (!p) return <div className="min-h-screen bg-black" />;

  const rows = [
    ['Client', p.client_name], ['Facility', p.facility_name], ['Business Type', p.business_type],
    ['Selected Package', `${p.selected_package?.toUpperCase()} CARE`],
    ['Service Frequency', p.service_frequency],
    ['Specialty Services', (p.specialty_services || []).join(', ') || '—'],
    ['Sustainability Options', (p.sustainability_options || []).join(', ') || '—'],
    ['Estimated Monthly Investment', p.estimated_low || p.estimated_high ? `$${Number(p.estimated_low).toLocaleString()} – $${Number(p.estimated_high).toLocaleString()}` : 'Confirmed after scope validation'],
    ['Contract Term', `${p.contract_term_months} months`],
    ['Exclusions', p.exclusions || 'Per executed service agreement'],
    ['Service Standards', p.service_standards],
    ['Account Contact', p.account_contact || 'NECS Account Management'],
  ];

  return (
    <div className="min-h-screen py-10 px-4" style={{ backgroundColor: '#141414' }} data-testid="proposal-page">
      <div className="max-w-3xl mx-auto rounded-xl overflow-hidden print:rounded-none" style={{ backgroundColor: '#1f1f1f', border: `1px solid ${GOLD}` }}>
        <div className="p-8">
          <h1 className="text-3xl font-black" style={{ color: GOLD, fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="proposal-title">
            NECS FACILITY CARE PROPOSAL
          </h1>
          <p className="text-xs text-zinc-500 mt-1">Noble Empire Cleaning Services, LLC · Prepared {String(p.created_at).slice(0, 10)} · {p.proposal_id}</p>
          <h3 className="text-white font-bold mt-6 mb-2 uppercase text-sm tracking-widest">Scope of Services</h3>
          <p className="text-sm text-zinc-300">{p.scope}</p>
          <table className="w-full mt-6 text-sm">
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k} className="border-b border-zinc-800">
                  <td className="py-2.5 pr-4 whitespace-nowrap align-top" style={{ color: GOLD }}>{k}</td>
                  <td className="py-2.5 text-white">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[11px] text-zinc-500 mt-6">
            Acceptance: this proposal becomes effective upon execution of the NECS service agreement. Final pricing is confirmed following scope validation and, when appropriate, a facility assessment by an NECS Account Manager.
          </p>
        </div>
      </div>
      <div className="max-w-3xl mx-auto flex gap-3 mt-5 print:hidden">
        <button onClick={() => window.print()} className="px-5 py-2.5 rounded-full text-sm font-bold" style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="proposal-print-btn">
          Print / Save as PDF
        </button>
        {p.client_email && (
          <button
            onClick={async () => {
              try { await apiFetch(`/api/commercial-quote/proposals/${id}/email`, { method: 'POST' }); toast.success('Emailed to client'); }
              catch (e) { toast.error(e.detail || 'Email failed'); }
            }}
            className="px-5 py-2.5 rounded-full text-sm font-bold border" style={{ borderColor: GOLD, color: GOLD }} data-testid="proposal-email-btn">
            Email to {p.client_email}
          </button>
        )}
      </div>
    </div>
  );
}
